import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { User, MapPin, ArrowLeft, ExternalLink, Sparkles, Mail } from "lucide-react";
import axios from "axios";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import BackBar from "../components/BackBar";
import LeadCapture from "../components/LeadCapture";
import Brand from "../components/Brand";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  apologist: "Apologist",
  wellness: "Wellness Coach",
  bible_teacher: "Bible Teacher",
  homeschool: "Homeschool / Family",
  marketplace: "Christian Marketplace",
  ministry: "Pastor / Ministry",
  coach: "Christian Coach",
  other: "Member",
};

export default function PublicProfile() {
  const { username } = useParams();
  const [member, setMember] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/u/${username}`)
      .then(r => setMember(r.data))
      .catch(() => setError("Member not found"))
      .finally(() => setLoading(false));
  }, [username]);

  if (loading) return (
    <div className="min-h-screen bg-ivory flex items-center justify-center text-red-wine font-serif-display text-2xl">Loading…</div>
  );

  if (error || !member) return (
    <div className="min-h-screen bg-ivory">
      <SiteNav />
      <BackBar />
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">404</div>
        <h1 className="font-serif-display text-4xl text-red-wine uppercase">Member not found</h1>
        <p className="text-foreground mt-3">We couldn't find <strong><Brand suffix=".com"/>/u/{username}</strong>. They may have changed their handle or removed their public page.</p>
        <Link to="/directory" className="btn-primary inline-block mt-6">Browse the Directory</Link>
      </div>
      <SiteFooter />
    </div>
  );

  const f = member.funnel;
  return (
    <div className="min-h-screen bg-ivory" data-testid="public-profile-page">
      <SiteNav />
      <BackBar fallback="/directory" label="← Browse Directory"/>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 pt-6 pb-10">
        <div className="rounded-2xl border-2 border-[#F39A1F]/40 bg-gradient-to-br from-[#FFFFFF] to-white p-6 md:p-8 shadow-xl">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden border-4 border-[#F39A1F] bg-white flex items-center justify-center shrink-0 shadow-md">
              {member.profile_photo
                ? <img src={member.profile_photo} alt="" className="w-full h-full object-cover"/>
                : <User className="w-16 h-16 text-foreground/40"/>}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">★ <Brand/> MEMBER · {CATEGORY_LABEL[member.profile_category] || "Member"}</div>
              <h1 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase leading-tight">{member.full_name}</h1>
              {member.headline && <div className="text-[#D7800A] italic font-semibold text-base md:text-lg mt-0.5">{member.headline}</div>}
              {member.location && <div className="text-sm text-foreground mt-1 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{member.location}</div>}
              {member.bio && <p className="text-sm md:text-base text-foreground mt-3 leading-relaxed max-w-2xl">{member.bio}</p>}
              <div className="text-sm tracking-wider-cap font-bold text-foreground/70 mt-3"><Brand suffix=".com"/>/u/{member.username}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Their funnel */}
      {f ? (
        <section className="max-w-5xl mx-auto px-6 md:px-8 pb-12" data-testid="public-profile-funnel">
          <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep mb-3">★ {member.full_name}'s Page</div>
          <div className="rounded-2xl overflow-hidden border-2 border-foreground/10 bg-white shadow-2xl">
            {f.image && (
              <div className="relative">
                <img src={f.image} alt="" className="w-full h-72 md:h-96 object-contain p-3 bg-white"/>
              </div>
            )}
            <div className="p-7 md:p-10">
              {f.headline && <h2 className="font-serif-display text-3xl md:text-5xl text-red-wine uppercase leading-tight">{f.headline}</h2>}
              {f.subheadline && <p className="text-base md:text-lg text-foreground mt-3 max-w-3xl">{f.subheadline}</p>}
              <Link to={`/f/${f.id}`} target="_blank" rel="noreferrer" data-testid="public-profile-funnel-cta" className="btn-primary inline-flex items-center gap-2 mt-6">
                Visit Their Full Page <ExternalLink className="w-4 h-4"/>
              </Link>
            </div>
          </div>
        </section>
      ) : (
        <section className="max-w-3xl mx-auto px-6 md:px-8 pb-12">
          <div className="rounded-2xl border-2 border-dashed border-[#F39A1F]/40 bg-white/40 p-8 text-center">
            <Sparkles className="w-10 h-10 text-[#F39A1F] mx-auto mb-2"/>
            <div className="font-serif-display text-2xl text-red-wine">Page coming soon.</div>
            <div className="text-sm text-foreground mt-1">{member.full_name} is still building their public page. Check back shortly.</div>
          </div>
        </section>
      )}

      {/* Footer CTA — invite the visitor to join */}
      <section className="max-w-3xl mx-auto px-6 md:px-8 pb-16">
        <div className="rounded-2xl bg-gradient-to-r from-[#0F172A] to-[#2A2521] text-white p-6 md:p-8 shadow-2xl border-2 border-[#F39A1F]/40 text-center">
          <Mail className="w-10 h-10 text-[#F39A1F] mx-auto mb-2"/>
          <div className="font-serif-display text-3xl uppercase">Want a page like this?</div>
          <p className="text-white/80 mt-1">7-Day Free Trial to start · <Brand suffix=".com"/>/u/your-name · Flat 50% — same for trial and paid affiliate.</p>
          <Link to="/signup?plan=free" className="btn-primary mt-5 inline-block">Start My 7-Day Free Trial</Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
